import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  TextField,
  CircularProgress,
} from '@mui/material';
import {
  Edit as ComposeIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  Chat as ChatIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
} from '@mui/icons-material';
import MessagesTimeline from './MessagesTimeline';
import MessagesTab from './MessagesTab';
import EmailThreadPanel from './EmailThreadPanel';
import EnhancedCommunicationComposer from './EnhancedCommunicationComposer';
import ScheduleCallDialog from './ScheduleCallDialog';
import api, { enhancedApi } from '../api';

const SMS_STATUS_ICONS = {
  sent: <ScheduleIcon sx={{ fontSize: 14, color: 'info.main' }} />,
  delivered: <CheckCircleIcon sx={{ fontSize: 14, color: 'success.main' }} />,
  failed: <ErrorIcon sx={{ fontSize: 14, color: 'error.main' }} />,
};

const formatSmsTime = (dateString) => {
  const d = new Date(dateString);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const CommunicationTab = ({
  client,
  onMeetingCreated,
  onUnreadChange,
  externalAction,
  onExternalActionConsumed,
}) => {
  const [composerOpen, setComposerOpen] = useState(false);
  const [composerMode, setComposerMode] = useState('create');
  const [composerPreset, setComposerPreset] = useState(null);
  const [selectedCommunication, setSelectedCommunication] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [scheduleCallOpen, setScheduleCallOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [overdueCount, setOverdueCount] = useState(0);
  const [commTab, setCommTab] = useState(0);
  const [smsMessages, setSmsMessages] = useState([]);
  const [smsLoading, setSmsLoading] = useState(false);
  const [smsBody, setSmsBody] = useState('');
  const [smsSending, setSmsSending] = useState(false);
  const hasMobile = !!(client?.mobile_phone || client?.mobile);

  // Fetch overdue follow-up count for the summary banner
  const fetchOverdueCount = async () => {
    if (!client?.id) return;
    try {
      const res = await api.get(`/api/clients/${client.id}/communications/?overdue_followups=true`);
      setOverdueCount(res.data?.total_count || 0);
    } catch {
      setOverdueCount(0);
    }
  };

  useEffect(() => {
    fetchOverdueCount();
  }, [client?.id, refreshTrigger]);

  useEffect(() => {
    if (externalAction?.key == null || !externalAction?.type) return;
    const { type } = externalAction;
    if (type === 'compose_email') {
      setCommTab(1);
      setComposerPreset(null);
      setComposerMode('create');
      setSelectedCommunication(null);
      setComposerOpen(true);
    } else if (type === 'request_docs') {
      setCommTab(1);
      setComposerPreset('document_request');
      setComposerMode('create');
      setSelectedCommunication(null);
      setComposerOpen(true);
    } else if (type === 'schedule_call') {
      setScheduleCallOpen(true);
    } else if (type === 'sms') {
      setCommTab(2);
    }
    queueMicrotask(() => onExternalActionConsumed?.());
  }, [externalAction?.key, externalAction?.type, onExternalActionConsumed]);

  const fetchSmsHistory = useCallback(async () => {
    if (!client?.id) return;
    try {
      setSmsLoading(true);
      const res = await enhancedApi.getSMSHistory(client.id);
      const list = res.data?.messages ?? res.data ?? [];
      setSmsMessages(Array.isArray(list) ? list : []);
    } catch {
      setSmsMessages([]);
    } finally {
      setSmsLoading(false);
    }
  }, [client?.id]);

  useEffect(() => {
    if (commTab === 2 && client?.id) {
      fetchSmsHistory();
      const interval = setInterval(fetchSmsHistory, 10000);
      return () => clearInterval(interval);
    }
  }, [commTab, client?.id, fetchSmsHistory]);

  const handleSendSms = async () => {
    if (!client?.id || !smsBody.trim() || !hasMobile) return;
    try {
      setSmsSending(true);
      await enhancedApi.sendSMS(client.id, smsBody.trim());
      setSmsBody('');
      setSnackbar({ open: true, message: 'SMS sent.', severity: 'success' });
      fetchSmsHistory();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'Failed to send SMS',
        severity: 'error',
      });
    } finally {
      setSmsSending(false);
    }
  };

  const handleCreateCommunication = (mode = 'create', communication = null) => {
    setComposerMode(mode);
    setSelectedCommunication(communication);
    setComposerPreset(null);
    setComposerOpen(true);
  };

  const handleComposerClose = () => {
    setComposerOpen(false);
    setComposerPreset(null);
  };

  const handleComposerSuccess = (data) => {
    setComposerOpen(false);
    setRefreshTrigger(prev => prev + 1);
    const isMeeting = data?.communication_type_name === 'Meeting' || data?.communication_type_name === 'Appointment';
    setSnackbar({
      open: true,
      message: isMeeting ? 'Meeting saved successfully.' : 'Message sent successfully.',
      severity: 'success',
    });
  };

  const handleCommunicationSelect = (communication, action) => {
    if (action === 'reply') {
      handleCreateCommunication('reply', communication);
    } else if (action === 'edit') {
      handleCreateCommunication('edit', communication);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 500 }}>
      {/* Overdue follow-up banner */}
      {overdueCount > 0 && (
        <Box
          sx={{
            px: 2,
            py: 1,
            bgcolor: 'error.lighter',
            borderBottom: '1px solid',
            borderColor: 'error.light',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexShrink: 0,
          }}
        >
          <WarningIcon sx={{ color: 'error.main', fontSize: 18 }} />
          <Typography variant="body2" sx={{ color: 'error.dark', fontWeight: 500, flex: 1 }}>
            {overdueCount} overdue follow-up{overdueCount > 1 ? 's' : ''} — check the Follow-Ups tab
          </Typography>
          <Button
            variant="contained"
            size="small"
            onClick={() => setCommTab(0)}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            View Follow-Ups
          </Button>
        </Box>
      )}

      {/* Tabs: Messages / Email / SMS / Quick Message */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Tabs value={commTab} onChange={(_, v) => setCommTab(v)} sx={{ minHeight: 40, px: 1, flex: 1 }}>
          <Tab label="Messages" sx={{ minHeight: 40, fontSize: '0.8125rem', textTransform: 'none', fontWeight: 600 }} />
          <Tab label="Email" sx={{ minHeight: 40, fontSize: '0.8125rem', textTransform: 'none', fontWeight: 600 }} />
          <Tab label="SMS" sx={{ minHeight: 40, fontSize: '0.8125rem', textTransform: 'none', fontWeight: 600 }} />
          <Tab label="Quick Message" sx={{ minHeight: 40, fontSize: '0.8125rem', textTransform: 'none', fontWeight: 600 }} />
        </Tabs>
        <Button
          variant="contained"
          size="small"
          startIcon={<ComposeIcon />}
          onClick={() => handleCreateCommunication('create')}
          sx={{ mr: 1, textTransform: 'none', fontWeight: 600 }}
        >
          Compose
        </Button>
      </Box>

      {/* Tab content */}
      <Box sx={{ flex: 1, minHeight: 0, mt: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {commTab === 0 && (
          <MessagesTimeline
            clientId={client?.id}
            clientName={client?.display_name}
            onCommunicationSelect={handleCommunicationSelect}
            onSendFirstMessage={() => handleCreateCommunication('create')}
            refreshTrigger={refreshTrigger}
          />
        )}
        {commTab === 1 && (
          <EmailThreadPanel
            clientId={client?.id}
            onCommunicationSelect={handleCommunicationSelect}
            refreshTrigger={refreshTrigger}
          />
        )}
        {commTab === 3 && (
          <MessagesTab client={client} onUnreadChange={onUnreadChange} />
        )}
        {commTab === 2 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
            {!hasMobile && (
              <Alert severity="warning" sx={{ mx: 2, mt: 2, flexShrink: 0 }}>
                No mobile number on file for this client. Add a mobile number in the client profile to send SMS.
              </Alert>
            )}
            <Box sx={{ flex: 1, overflow: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
              {smsLoading && smsMessages.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={32} />
                </Box>
              ) : smsMessages.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  No SMS messages yet.
                </Typography>
              ) : (
                smsMessages.map((msg, idx) => {
                  const isOutbound = msg.direction === 'outbound' || msg.direction === 'out' || msg.from_me;
                  const statusIcon = SMS_STATUS_ICONS[msg.status] || null;
                  return (
                    <Box
                      key={msg.id ?? msg.sid ?? idx}
                      sx={{
                        alignSelf: isOutbound ? 'flex-end' : 'flex-start',
                        maxWidth: '85%',
                        px: 2,
                        py: 1.5,
                        borderRadius: 2,
                        bgcolor: isOutbound ? 'primary.light' : 'grey.100',
                      }}
                    >
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {msg.body ?? msg.message ?? msg.content ?? ''}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, justifyContent: isOutbound ? 'flex-end' : 'flex-start' }}>
                        <Typography variant="caption" color="text.secondary">
                          {formatSmsTime(msg.timestamp ?? msg.created_at ?? msg.date_sent ?? Date.now())}
                        </Typography>
                        {isOutbound && statusIcon}
                      </Box>
                    </Box>
                  );
                })
              )}
            </Box>
            <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', flexShrink: 0 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                <TextField
                  multiline
                  maxRows={4}
                  placeholder="Type a message…"
                  value={smsBody}
                  onChange={(e) => setSmsBody(e.target.value)}
                  disabled={!hasMobile}
                  inputProps={{ maxLength: 160 }}
                  size="small"
                  sx={{ flex: 1 }}
                />
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {smsBody.length} / 160
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={handleSendSms}
                    disabled={!smsBody.trim() || !hasMobile || smsSending}
                    sx={{ minWidth: 80 }}
                  >
                    {smsSending ? 'Sending…' : 'Send'}
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      <ScheduleCallDialog
        open={scheduleCallOpen}
        onClose={() => setScheduleCallOpen(false)}
        clientId={client?.id}
        clientName={client?.display_name}
        onSuccess={() => {
          setRefreshTrigger((prev) => prev + 1);
          setSnackbar({ open: true, message: 'Meeting scheduled successfully.', severity: 'success' });
        }}
        onMeetingCreated={onMeetingCreated}
      />

      <EnhancedCommunicationComposer
        open={composerOpen}
        onClose={handleComposerClose}
        clientId={client?.id}
        clientName={client?.display_name}
        mode={composerMode}
        communication={selectedCommunication}
        onSuccess={handleComposerSuccess}
        onMeetingCreated={onMeetingCreated}
        presetType={composerPreset === 'document_request' ? 'document_request' : null}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CommunicationTab;
